import React from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Brain,
  Building,
  Code,
  Users,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import Diagram from "../assets/Images/Diagram.jpg";

export const TrainingStrategy = () => {
  const pillars = [
    {
      icon: Building,
      title: "Business Skills",
      description:
        "Master the Software Development Life Cycle (SDLC). Gain direct experience in stakeholder management, project scoping, and delivering measurable business value.",
      highlights: [
        "Stakeholder Engagement",
        "Project planning",
        "Requirement Engineering",
        "ROI understanding",
      ],
      color: "from-blue-500 to-blue-600",
    },
    {
      icon: Sparkles,
      title: "Leveraging AI",
      description:
        "Master AI tools and automation to Integrate advanced LLMs and automated CI/CD workflows into your daily development cycle to achieve enterprise-grade efficiency. Learn to integrate AI into development workflows and business solutions.",
      highlights: [
        "LLM Implementation",
        "DevOps Automation",
        "ML integration",
        "AI workflow optimization",
      ],
      color: "from-purple-500 to-purple-600",
    },
    {
      icon: Code,
      title: "Technology Skills",
      description:
        "Construct scalable, resilient systems using modern microservices architecture and cloud-native practices currently dominating the tech landscape.",
      highlights: [
        "Distributed Systems",
        "Cloud-Native Infrastructure",
        "DevOps practices",
        "System architecture",
      ],
      color: "from-slate-600 to-slate-700",
    },
    {
      icon: Users,
      title: "Soft Skills",
      description:
        "Cultivate the professional maturity required for high-growth environments, focusing on cross-functional teamwork, architectural presentation, and an engineering first mindset",
      highlights: [
        "Team collaboration",
        "Presentation skills",
        "Problem solving",
        "Leadership mindset",
      ],
      color: "from-green-500 to-green-600",
    },
  ];

  return (
    <section
      id="training-strategy"
      className="py-20 bg-gradient-to-br from-slate-50 to-white"
    >
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center space-y-6 mb-16">
          <h2 className="text-4xl font-bold text-gray-900">
            The Engineering Excellence Framework
          </h2>

          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            Unlike traditional coding bootcamps, we prepare you for the complete
            professional landscape. Our comprehensive approach ensures you're
            not just a coder, but a well-rounded technology professional.
          </p>
        </div>

        {/* Strategy Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {pillars.map((pillar, index) => {
            const IconComponent = pillar.icon;
            return (
              <Card
                key={index}
                className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-slate-200 overflow-hidden"
              >
                <CardContent className="p-0">
                  {/* Header with Icon */}
                  <div
                    className={`bg-gradient-to-r ${pillar.color} p-8 text-white relative overflow-hidden`}
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16" />
                    <div className="relative z-10">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4">
                        <IconComponent size={32} className="text-white" />
                      </div>
                      <h3 className="text-2xl font-bold mb-2">
                        {pillar.title}
                      </h3>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8 space-y-6">
                    <p className="text-gray-700 leading-relaxed text-lg">
                      {pillar.description}
                    </p>

                    {/* Highlights */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">
                        Key Focus Areas:
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        {pillar.highlights.map((highlight, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 text-sm text-gray-600"
                          >
                            <div className="w-2 h-2 bg-slate-400 rounded-full flex-shrink-0" />
                            {highlight}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Integration Message */}
        {/* Diagram */}
        <div id="whyGj" className="p-10 flex justify-center">
          <img
            src={Diagram}
            alt="Training strategy diagram"
            className="max-w-full md:max-w-5xl rounded-2xl"
          />
        </div>

        {/* Bottom Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mt-20 text-center">
          <div className="space-y-2">
            <div className="text-3xl md:text-4xl font-bold text-slate-700">
              4+
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              Years of GJ Global Heritage
            </p>
          </div>

          <div className="space-y-2">
            <div className="text-3xl md:text-4xl font-bold text-slate-700">
              1 : 1
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              Expert Guidance
            </p>
          </div>

          <div className="space-y-2">
            <div className="text-3xl md:text-4xl font-bold text-slate-700">
              40+
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              Live Project Assignments Completed
            </p>
          </div>

          <div className="space-y-2">
            <div className="text-3xl md:text-4xl font-bold text-slate-700">
              25+
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              Technology-Focused Skill Tracks
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
